/* Editorial featured products carousel (snippets/fp-editorial-carousel.liquid). No dependencies. */
(function () {
  if (window.customElements.get('fpec-carousel')) return;

  class FpecCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fpec-track]');
      if (!this.track) return;

      if (!this.bound) {
        this.bound = true;
        this.prevBtn = this.querySelector('[data-fpec-prev]');
        this.nextBtn = this.querySelector('[data-fpec-next]');
        this.dots = this.querySelector('[data-fpec-dots]');
        this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-fpec-slide]'));
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.pages = [0];

        this.build = this.build.bind(this);
        this.onScroll = this.onScroll.bind(this);

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('keydown', (event) => {
          if (event.target !== this.track) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          }
        });
        this.initWishlist();
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = this.resizeObserver || new ResizeObserver(this.build);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.build);
      }

      this.build();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.build);
    }

    behavior() {
      return this.motion && this.motion.matches ? 'auto' : 'smooth';
    }

    metrics() {
      const style = window.getComputedStyle(this.track);
      const padStart = parseFloat(style.paddingLeft) || 0;
      const padEnd = parseFloat(style.paddingRight) || 0;
      return {
        gap: parseFloat(style.columnGap) || 0,
        visible: this.track.clientWidth - padStart - padEnd,
        max: Math.max(0, this.track.scrollWidth - this.track.clientWidth),
      };
    }

    build() {
      if (!this.slides || !this.slides.length) return;

      const m = this.metrics();
      const first = this.slides[0].offsetLeft;
      const slideWidth = this.slides[0].getBoundingClientRect().width || 1;
      const perPage = Math.max(1, Math.floor((m.visible + m.gap + 1) / (slideWidth + m.gap)));
      const pages = [];

      if (m.max > 2) {
        for (let i = 0; i < this.slides.length; i += perPage) {
          const pos = Math.min(this.slides[i].offsetLeft - first, m.max);
          if (!pages.length || pos - pages[pages.length - 1] > 2) pages.push(pos);
          if (pos >= m.max) break;
        }
        const last = pages[pages.length - 1];
        if (last < m.max - 2) {
          if (m.max - last < slideWidth / 2 && pages.length > 1) pages[pages.length - 1] = m.max;
          else pages.push(m.max);
        }
      }

      this.pages = pages.length ? pages : [0];
      this.classList.add('fpec--ready');
      this.classList.toggle('fpec--static', this.pages.length < 2);
      this.renderDots();
      this.positionArrows();
      this.update();
    }

    renderDots() {
      if (!this.dots) return;
      const count = this.pages.length;
      this.dots.hidden = count < 2;
      if (this.dots.childElementCount === count) return;

      this.dots.innerHTML = '';
      if (count < 2) return;

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fpec__dot';
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1) + ' of ' + count);
        dot.setAttribute('aria-controls', this.track.id);
        dot.addEventListener('click', () => this.goTo(i));
        this.dots.appendChild(dot);
      }
    }

    positionArrows() {
      const media = this.track.querySelector('.fpec-card__media');
      const stage = this.track.parentElement;
      if (!media || !stage) return;
      const top = media.getBoundingClientRect().top - stage.getBoundingClientRect().top + media.offsetHeight / 2;
      if (top > 0) stage.style.setProperty('--fpec-arrow-top', Math.round(top) + 'px');
    }

    nearestIndex() {
      const x = this.track.scrollLeft;
      let best = 0;
      let distance = Infinity;
      this.pages.forEach((pos, i) => {
        const d = Math.abs(pos - x);
        if (d < distance) {
          distance = d;
          best = i;
        }
      });
      return best;
    }

    step(direction) {
      const x = this.track.scrollLeft;
      let target;
      if (direction > 0) {
        target = this.pages.findIndex((pos) => pos > x + 2);
        if (target === -1) target = this.pages.length - 1;
      } else {
        target = -1;
        this.pages.forEach((pos, i) => {
          if (pos < x - 2) target = i;
        });
        if (target === -1) target = 0;
      }
      this.goTo(target);
    }

    goTo(index) {
      const pos = this.pages[Math.max(0, Math.min(this.pages.length - 1, index))];
      this.track.scrollTo({ left: pos, behavior: this.behavior() });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    update() {
      const x = this.track.scrollLeft;
      const max = this.metrics().max;
      const current = this.nearestIndex();

      if (this.prevBtn) this.prevBtn.disabled = x <= 2;
      if (this.nextBtn) this.nextBtn.disabled = x >= max - 2;
      if (this.dots) {
        Array.prototype.forEach.call(this.dots.children, (dot, i) => {
          dot.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }
    }

    /* Wishlist: uses the store's existing Swym Wishlist Plus app. Buttons stay hidden if Swym is not available. */
    initWishlist() {
      const buttons = Array.prototype.slice.call(this.querySelectorAll('[data-fpec-wish]'));
      if (!buttons.length) return;

      const setState = (button, active) => {
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        const title = button.getAttribute('aria-label').replace(/^(Add|Remove) /, '').replace(/ (to|from) wishlist$/, '');
        button.setAttribute('aria-label', (active ? 'Remove ' : 'Add ') + title + (active ? ' from wishlist' : ' to wishlist'));
      };

      const setup = (swat) => {
        if (this.wishReady || !swat || typeof swat.addToWishList !== 'function') return;
        this.wishReady = true;

        buttons.forEach((button) => {
          button.hidden = false;
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const product = {
              epi: Number(button.dataset.variantId),
              empi: Number(button.dataset.productId),
              du: button.dataset.productUrl,
            };
            const active = button.getAttribute('aria-pressed') === 'true';
            if (active && typeof swat.removeFromWishList === 'function') {
              swat.removeFromWishList(product, () => setState(button, false));
            } else if (!active) {
              swat.addToWishList(product, () => setState(button, true));
            }
          });
        });

        if (typeof swat.fetch === 'function') {
          swat.fetch((items) => {
            const ids = new Set((items || []).map((item) => String(item.empi)));
            buttons.forEach((button) => setState(button, ids.has(button.dataset.productId)));
          });
        }
      };

      const trySetup = () => {
        if (window._swat && typeof window._swat.addToWishList === 'function') {
          setup(window._swat);
          return true;
        }
        return false;
      };

      if (!trySetup()) {
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push(setup);
        let tries = 0;
        const timer = window.setInterval(() => {
          tries += 1;
          if (this.wishReady || trySetup() || tries > 40) window.clearInterval(timer);
        }, 500);
      }
    }
  }

  window.customElements.define('fpec-carousel', FpecCarousel);
})();
